<template>
	<div class="search-form">
		<Loading
			v-if="redirecting"
			class="search-form__loading"
		/>
		<ThemeLandingPage @ThemeLandingPage:Submit="onSubmit" />
	</div>
</template>

<script lang="ts" setup>
import ThemeLandingPage from '@lmt-rpb/ThemeLandingPage/ThemeLandingPage.vue';
import Loading from '@lmt-rpb/Loading/Loading.vue';
import type { SearchFormDataType } from '@interfaces/search-form';
import { objectToQuery, formToParams } from '@services/transform';
import { updateURL } from '@components/common/services/url';
import { throttle } from '@utils/utils';
import { EventBus } from '@global-js/event-bus';
import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from '@/components/common/store';
import { fetchTourOperatorInventory } from '@services/tourOperatorInventoryService';
import { getBaseUrl } from '@/js/utils/environmentUtils';

const store = useStore();

const emit = defineEmits(['window:resize']);

const redirecting = ref(false);

const handlePersistedCache = (event: PageTransitionEvent): void => {
	if (event.persisted) {
		document.location.reload();
	}
};

const formData = computed((): SearchFormDataType => store.state.searchMask);
const baseUrl = getBaseUrl();

const targetUrl = computed((): string => {
	return formData.value.destination?.url ? `${baseUrl}${formData.value.destination.url}` : `${baseUrl}/region/`;
});

const updateIbeType = (isHotelOnly: boolean): void => {
	store.commit('searchMask/updateFormData', { onlyHotel: isHotelOnly });
};

const handleResize = (event: Event): void => {
	emit('window:resize', event);
};

const onSubmit = (): void => {
	const body = store.getters['searchMask/submitData'];
	const params = formToParams(body, 'search');

	const query = objectToQuery(params);

	const searchTerm = formData.value.searchTerm;
	const travelType = formData.value.onlyHotel ? 'hotel' : 'package';
	let url = '';

	url = `${targetUrl.value}?${query}` + (searchTerm ? `&term=${encodeURIComponent(searchTerm)}` : '');

	// When redirecting to our own hotel pages, add the ibe parameter
	if (url.indexOf('ibe=') === -1 && url.indexOf('/hotel/') !== -1) {
		url += url.indexOf('?') !== -1 ? '&' : '?';
		url += 'ibe=' + travelType;
	}

	if (!store.state.config.redirectOnSubmit) {
		console.log('redirect: ', url);
	} else {
		document.body.style.opacity = '0.3';
		redirecting.value = true;
		window.location.href = url;
	}
};

const fetchTourOperators = (): void => {
	fetchTourOperatorInventory({});
};

onBeforeMount((): void => {
	// Prevents endless loading spinner if user clicks back fast from ibe on >= iOS 13
	window.addEventListener('pageshow', handlePersistedCache);

	throttle(handleResize, 250);
	window.addEventListener('resize', handleResize);
	EventBus.$on('HotelOnly:Change', updateIbeType);
});

onMounted((): void => {
	fetchTourOperators();
});

onBeforeUnmount((): void => {
	window.removeEventListener('pageshow', handlePersistedCache);
	window.removeEventListener('resize', handleResize);
	EventBus.$off('HotelOnly:Change', updateIbeType);
});

watch(
	() => formData.value,
	() => {
		updateURL(formToParams(formData.value, 'searchLandingPage'));
		fetchTourOperators();
	},
	{ deep: true }
);
</script>

<style lang="scss" scoped>
.search-form {
	scrollbar-color: $color-primary transparent;
	scrollbar-width: thin;
	-webkit-tap-highlight-color: transparent;

	::-webkit-scrollbar {
		width: 1.2rem;
	}

	::-webkit-scrollbar-thumb {
		border-radius: $border-radius-small;
		background-color: $color-primary;
	}
}

.search-form__loading {
	position: absolute;
	top: 50%;
	z-index: 100;
	transform: translateY(-31%);
	margin-left: auto;
	margin-right: auto;
	left: 0;
	right: 0;
}
</style>
